/**
 * Lesepfad Phase 11/13/14/26 — entity_* oder Blob/domain_aggregate (konfigurierbar).
 */

import { isDomainAggregateIndexEnabled } from '../infrastructure/storage/domainAggregateStore';
import {
  isEntityStorePrimaryReadEnabled,
  isEntityStoreReadEnabled
} from '../infrastructure/storage/entityAggregateStore';
import { isEntityStoreNoKvBlobEnabled } from '../infrastructure/storage/businessPayloadMeta';
import { mergeIndexedWithBlobAuthoritative } from './businessStateSync';

/**
 * Phase 26 — entity_* allein (kein Blob, kein domain_aggregate).
 * @param {string} businessId
 * @param {(businessId: string) => Promise<object[]>} listFromEntityStore
 */
export async function readEntityOnly(businessId, listFromEntityStore) {
  if (!businessId || !listFromEntityStore) return [];
  try {
    return await listFromEntityStore(businessId);
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[canonicalEntityRead] entity-only read failed:', err?.message || err);
    }
    return [];
  }
}

/**
 * @param {string} businessId
 * @param {() => object[]} readFromBlob
 * @param {(businessId: string) => Promise<object[]>} listFromAggregate
 * @param {(businessId: string) => Promise<object[]>} [listFromEntityStore]
 * @param {string[]} [idKeys]
 */
export async function readBlobFirstWithAggregateFallback(
  businessId,
  readFromBlob,
  listFromAggregate,
  listFromEntityStore = null,
  idKeys = ['id']
) {
  if (!businessId) return [];

  // Phase 26: entity_* is sole authority — never serve or resync from legacy blob.
  if (isEntityStoreNoKvBlobEnabled() && listFromEntityStore) {
    return readEntityOnly(businessId, listFromEntityStore);
  }

  const fromBlob = readFromBlob(businessId);

  if (isEntityStorePrimaryReadEnabled() && listFromEntityStore) {
    try {
      const fromEntityStore = await listFromEntityStore(businessId);
      if (fromEntityStore.length > 0) {
        if (fromBlob.length > 0) {
          return mergeIndexedWithBlobAuthoritative(fromEntityStore, fromBlob, ...idKeys);
        }
        return fromEntityStore;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[canonicalEntityRead] entity store primary read failed:', err?.message || err);
      }
    }
  }

  if (fromBlob.length > 0) return fromBlob;

  if (isEntityStoreReadEnabled() && listFromEntityStore) {
    try {
      const fromEntityStore = await listFromEntityStore(businessId);
      if (fromEntityStore.length > 0) return fromEntityStore;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[canonicalEntityRead] entity store fallback failed:', err?.message || err);
      }
    }
  }

  if (!isDomainAggregateIndexEnabled()) return fromBlob;
  try {
    return await listFromAggregate(businessId);
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[canonicalEntityRead] aggregate fallback failed:', err?.message || err);
    }
    return fromBlob;
  }
}
